import { c as _c } from "react/compiler-runtime";
import type { PrV2FileTreeOpenBreakpoint } from '../../file-tree-v2/pr-v2-file-tree-utils';
import type { PrV2GroupGuidePlacement } from './pr-v2-changes-group-skeleton';
import { PrV2MergeabilityBadgeSkeleton } from '../../header/pr-v2-mergeability-badge';
import { PR_V2_CONTENT_CLASS } from '../pr-v2-layout-classes';
import { PrV2ChangesGroupSkeleton } from './pr-v2-changes-group-skeleton';
import { PrV2FileTreeSkeleton, PrV2FileTreeToolbarSkeleton } from './pr-v2-file-tree-skeleton';
import { ChatComposerSkeleton } from '@/components/chat/chat-composer-skeleton';
import { CHAT_SUGGESTIONS_LAYER_OUTER_CLASS, ChatSuggestionsSkeleton } from '@/components/chat/chat-suggestions-skeleton';
import { Skeleton } from '@/components/ui/skeleton';

// Height a long PR description collapses to; the skeleton reserves the same box.
export const DESCRIPTION_COLLAPSED_HEIGHT_PX = 260;

/**
 * Generates deterministic skeleton keys for repeated placeholders.
 */
function buildSequentialKeys(prefix: string, count: number): string[] {
  return Array.from({
    length: count
  }, (_, position) => `${prefix}-${position + 1}`);
}
const REVIEWER_SKELETON_KEYS = buildSequentialKeys('reviewer', 2);
const DISCUSSION_ROW_SKELETON_KEYS = buildSequentialKeys('discussion', 2);
const DEFAULT_CHANGES_GROUP_FILE_COUNT = 2;
export function PrV2ChangesContentSkeleton(t0) {
  const $ = _c(3);
  const {
    fileCount: t1,
    groupGuide: t2
  } = t0;
  const fileCount = t1 === undefined ? DEFAULT_CHANGES_GROUP_FILE_COUNT : t1;
  const groupGuide = t2 === undefined ? "card" : t2;
  let t3;
  if ($[0] !== fileCount || $[1] !== groupGuide) {
    t3 = <div data-testid="pr-v2-comparison-loading-skeleton" className="@container/pr-diff mr-1 space-y-6 pl-3 pr-2 pt-4 pb-10 md:pl-0.5"><PrV2ChangesGroupSkeleton fileCount={fileCount} groupGuide={groupGuide} /></div>;
    $[0] = fileCount;
    $[1] = groupGuide;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function PrV2HeaderSkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-8 w-4/5" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="space-y-2">{t0}<div className="flex items-center gap-2"><Skeleton className="h-5 w-28 rounded-md" /><Skeleton className="h-4 w-4 rounded-full" /><Skeleton className="h-5 w-24 rounded-md" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PrV2MergeabilityBadgeSkeleton />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <section data-testid="pr-v2-header-skeleton" className="space-y-4">{t1}<div className="flex flex-wrap items-center gap-4">{t2}<div className="flex items-center gap-2"><Skeleton className="h-4 w-4 rounded-full" /><Skeleton className="h-4 w-24" /></div><Skeleton className="h-4 w-28" /></div></section>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export function PrV2SidebarSkeleton() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex w-full flex-row items-start justify-between md:w-[220px]"><Skeleton className="mb-3 h-3 w-16" /><Skeleton className="h-6 w-6 rounded-md" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <section className="w-full md:w-[220px]">{t0}<div className="space-y-2 pr-0.5">{REVIEWER_SKELETON_KEYS.map(_temp)}</div></section>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <aside data-testid="pr-v2-sidebar-skeleton" className="pb-3 pl-0 md:pb-0">{t1}<section className="mt-6 mb-3 flex h-4 w-full items-center justify-between md:w-[220px]"><Skeleton className="h-3 w-14" /><Skeleton className="h-3 w-16" /></section></aside>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp(reviewerKey) {
  return <div key={reviewerKey} className="flex items-center justify-between"><div className="flex min-w-0 flex-1 items-center gap-2"><Skeleton className="h-5 w-5 rounded-full" /><Skeleton className="h-4 w-24" /></div><Skeleton className="h-4 w-4 rounded-full" /></div>;
}
export function PrV2DescriptionSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      height: DESCRIPTION_COLLAPSED_HEIGHT_PX
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div data-testid="pr-v2-description-skeleton" className="group prose prose-invert -ml-2 w-[calc(100%+1rem)] max-w-[840px]"><div className="flex flex-col gap-6 overflow-hidden rounded-md px-3 py-1 md:py-2" style={t0}><div className="space-y-2"><Skeleton className="h-4 w-11/12" /><Skeleton className="h-4 w-10/12" /><Skeleton className="h-4 w-9/12" /></div><Skeleton className="min-h-0 w-full flex-1 rounded-md" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PrV2TabsListSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div data-testid="pr-v2-tabs-list-skeleton" className="col-start-1 col-end-12 mb-5 flex w-full items-start justify-start rounded-none border-b border-zinc-800 bg-transparent p-0 md:col-start-2 md:col-end-10 2xl:mx-auto 2xl:w-[1000px]"><div className="flex items-center gap-6 py-2"><Skeleton className="h-4 w-16" /><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-14" /></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function PrV2ReviewSubheaderSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div data-testid="pr-v2-review-subheader-skeleton" className="sticky top-0 z-30 flex min-h-9 w-full items-center border-b border-zinc-800/60 bg-background"><div className="grid min-w-0 flex-1 grid-cols-12 gap-4"><div className={`${PR_V2_CONTENT_CLASS} flex h-9 items-center gap-6`}><Skeleton className="h-4 w-16" /><Skeleton className="h-4 w-20" /></div></div><PrV2FileTreeToolbarSkeleton /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function PrV2ChangesPaneSkeleton(t0) {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    fileCount: t2,
    groupGuide: t3,
    fileTreeOpenFrom: t4,
    compact: t5
  } = t1;
  const fileCount = t2 === undefined ? 1 : t2;
  const groupGuide = t3 === undefined ? "card" : t3;
  const fileTreeOpenFrom = t4 === undefined ? "xl" : t4;
  const compact = t5 === undefined ? false : t5;
  let t6;
  if ($[2] !== fileCount || $[3] !== groupGuide) {
    t6 = <div data-testid="pr-v2-changes-content-skeleton" className="@container/pr-diff min-w-0 flex-1"><div className="space-y-6 px-3 pb-10 pt-4 md:pl-0.5 md:pr-2"><PrV2ChangesGroupSkeleton fileCount={fileCount} groupGuide={groupGuide} /></div></div>;
    $[2] = fileCount;
    $[3] = groupGuide;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== compact || $[6] !== fileTreeOpenFrom) {
    t7 = !compact ? <PrV2FileTreeSkeleton openFrom={fileTreeOpenFrom} /> : null;
    $[5] = compact;
    $[6] = fileTreeOpenFrom;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <div data-testid="pr-v2-changes-pane-skeleton" className="relative w-full max-w-none"><div className="relative flex w-full max-w-none gap-0">{t6}{t7}</div></div>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  return t8;
}
export function PrV2DiscussionPaneSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="space-y-3">{DISCUSSION_ROW_SKELETON_KEYS.map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div data-testid="pr-v2-discussion-pane-skeleton" className="space-y-4">{t0}<div className="rounded-md border border-zinc-800 p-3"><Skeleton className="h-20 w-full" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp2(discussionRowKey) {
  return <div key={discussionRowKey} className="rounded-md border border-zinc-800 p-3"><div className="mb-2 flex items-center gap-2"><Skeleton className="h-6 w-6 rounded-full" /><Skeleton className="h-4 w-28" /><Skeleton className="h-4 w-20" /></div><div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-4/5" /></div></div>;
}
export function PrV2SidebarRightSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><div data-testid="pr-v2-sidebar-right-skeleton" className={CHAT_SUGGESTIONS_LAYER_OUTER_CLASS}><ChatSuggestionsSkeleton /></div><ChatComposerSkeleton /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}